import { BaseViewStore } from 'core/stores';
import { CarrierEditForm } from 'modules/carrier/forms';
import { CarrierStore } from 'modules/carrier/stores';
import { LookupStore } from 'modules/lookup/stores';
import { action, observable, makeObservable } from 'mobx';
import { FormHelperService } from 'common/data';
import { ApplicationStore } from 'platform/application/stores';
import { lookupRouteName, carrierTypes, sourceOfChange } from 'common/constants';
import { formInvalidate, translate, isSuperAdminOrganization, notificationError, isSome } from 'core/utils';
import { ConfirmModalParams } from 'core/models';
import { localStorageHandler } from 'core/services';
import { BaasicDropdownViewStore } from 'common/stores';
import { CredentialsValidationStore } from 'modules/credentials-validation/stores';
import { removeNullOrEmptyStringProperties } from 'core/utils/removeNullOrEmptyStringProperties';
import _ from 'lodash';

class CarrierEditViewStore extends BaseViewStore {
	@observable isThirdParty = false;
	@observable isPartialThirdParty = false;
	@observable isAuthorized = false;
	@observable isExternalAccount = false;
	@observable sellerId = null;
	carrierTypeDisabled = false;
	carrierTypeOptions = [];

	shippingAccountOptions = [];
	carrier = null;
	isEditMode = false;

	form = new CarrierEditForm({
		onSuccess: async () => {
			await this.createOrUpdateCarrier();
		},
	});

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.parentView = rootStore.carrierListViewStore;
		this.carrierStore = new CarrierStore(rootStore);
		this.lookupStore = new LookupStore(rootStore);
		this.formHelperService = new FormHelperService();
		this.credentialsValidationStore = new CredentialsValidationStore(rootStore);
		this.applicationStore = new ApplicationStore(rootStore);
		this.modalTitle =
			this.rootStore.routerStore.routerState.queryParams['create'] !== undefined
				? 'CARRIERVIEW.CREATECARRIER'
				: 'CARRIERVIEW.EDITCARRIER';
		this.isSuperAdminOrganization = isSuperAdminOrganization(rootStore);
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		this.suspend();
		this.form.state.options.set({
			validateOnBlur: false,
			showErrorsOnReset: false,
			validateOnChange: true,
		});
		this.warehouseDropdownStore = new BaasicDropdownViewStore(null, false);
		await this.warehouseDropdownStore.initialize({
			field: this.form.$('carrierWarehouses'),
			store: new ApplicationStore(this.rootStore),
			itemTemplate: {
				value: 'id',
				label: 'applicationTitle',
			},
			filterProperties: {
				warehouseOnly: true,
				isOrganizationActive: true,
				includeOnlySellerWarehouses: !this.isSuperAdminOrganization || this.getEditMode() === 'edit',
				embed: ['organizationType'],
			},
			isMulti: true,
		});
		await this.getCarrierTypes();
		if (this.getEditMode() === 'edit') {
			await this.getCarrier(this.rootStore.routerStore.routerState.queryParams.editId, {
				embed: [
					'carrierType',
					'shippingAccount',
					'shippingAccount.carrierType',
					'carrierWarehouses',
					'carrierWarehouses.warehouse',
				],
			});
			this.setBillToFieldsRequired(this.form.$('isPartialThirdParty').value);
			this.warehouseDropdownStore.filter.sellerWarehousesOnlyOrganizationId = this.carrier.sellerId;
			await this.warehouseDropdownStore.reload();
			const fieldValues = _.map(this.carrier.carrierWarehouses, carrierWarehouse => {
				return {
					value: carrierWarehouse.warehouseId,
					label: carrierWarehouse.warehouse.applicationTitle,
				};
			});
			this.warehouseDropdownStore.setValue(fieldValues);
		} else {
			this.form.clear();
			this.form.$('isPartialThirdParty').value = false;
			this.form.$('isPercentage').value = false;
			if (this.isSuperAdminOrganization) {
				this.form.$('seller').set('rules', 'required');
			}
		}
		await this.getCarrierShippingAccounts();
		if (this.isSuperAdminOrganization && !this.isEditMode) {
			await this.getSellerOrganizations();
		}

		this.resume();
	};

	@action.bound
	validateCredentials = async () => {
		this.suspend();
		try {
			let data = this.form.values();
			let credentials = await this.prepareCredentialsForValidation(data);
			var testConnectionResult = await this.credentialsValidationStore.validateCarrier({
				isCarrierPartialThirdParty: data.shippingAccount ? true : false,
				carrierTypeAbrv: data.carrierType ? data.carrierType.extra.abrv : data.shippingAccount.extra.carrierType.abrv,
				...credentials,
			});
			testConnectionResult.isSuccess
				? this.rootStore.notificationStore.success(['MESSAGES.CARRIER.CONNECTIONVALID'])
				: this.rootStore.notificationStore.error([testConnectionResult.message]);
		} catch (err) {
			this.rootStore.notificationStore.error(['MESSAGES.CARRIER.CONNECTIONINVALID']);
		}
		this.resume();
	};

	@action.bound
	prepareCredentialsForValidation = async data => {
		if (data.shippingAccount) {
			if (this.getEditMode() === 'edit') {
				let shippingAccountCredentials = data.shippingAccount.extra.json
					? JSON.parse(data.shippingAccount.extra.json)
					: data.shippingAccount.extra;
				return { ...shippingAccountCredentials, ...data.thirdPartyBilling };
			} else {
				return { ...data.shippingAccount.extra, ...data.thirdPartyBilling };
			}
		} else if (data.carrierType.label === carrierTypes.fedex) {
			return data.fedex;
		} else if (data.carrierType.label === carrierTypes.ups) {
			return data.ups;
		} else if (data.carrierType.label === carrierTypes.stamps) {
			return data.stamps;
		}
	};

	@action.bound
	getSellerOrganizations = async () => {
		await this.applicationStore.find({ page: 1, rpp: 15, sellerOnly: true });
		this.sellerOptions = _.map(this.applicationStore.data, item => {
			return { id: item.id, label: item.applicationTitle };
		});
		const seller = localStorageHandler.get('selectedSeller');
		if (seller) {
			if (this.sellerOptions.some(o => o.id === seller.value)) {
				this.onSelectSeller({ id: seller.value, label: seller.label }, this.form.$('seller'));
			}
		}
	};

	@action.bound
	getCarrier = async (id, options) => {
		await this.carrierStore.getCarrier(id, options);
		let { carrierWarehouses, ...carrier } = this.carrierStore.item; // dont update carrierWarehouses because it breaks multi-select validation
		const carrierCredentials = this.createCredentials(carrier);
		carrier = { ...carrier, ...carrierCredentials };
		this.formHelperService.updateForm(this.form, carrier, true);
		this.form.$('carrierType').value = {
			value: carrier.carrierType.id,
			label: carrier.carrierType.name,
			extra: carrier.carrierType,
		};
		this.form.$('surplusCharge').value = carrier.surplusCharge;
		this.form.$('isPercentage').value = carrier.isPercentage;
		this.isThirdParty = carrier.isThirdParty;
		this.isPartialThirdParty = carrier.isPartialThirdParty;
		this.form.$('isPartialThirdParty').value = carrier.isPartialThirdParty;
		this.carrierTypeDisabled = true;
		if (this.isThirdParty) {
			if (this.isPartialThirdParty && carrier.shippingAccount != null) {
				this.form.$('shippingAccount').value = {
					value: carrier.shippingAccount.id,
					label: carrier.shippingAccount.name,
					extra: carrier.shippingAccount,
				};
			}
		}
		this.carrier = carrier;
		this.carrier.carrierWarehouses = carrierWarehouses;
		this.sellerId = this.carrier.sellerId;

		if (carrier.carrierType.name === carrierTypes.ups) {
			this.isExternalAccount = carrier.isExternalAccount;
		}
	};

	@action.bound
	createCredentials = carrier => {
		let carrierCredentials = {};
		if (this.isPartialThirdParty) {
			carrierCredentials = {
				thirdPartyBilling: {
					...carrier.thirdPartyBilling,
				},
			};
		} else {
			if (carrier.carrierType.name === carrierTypes.fedex) {
				carrierCredentials = {
					fedex: {
						clientId: carrier.clientId,
						clientSecret: carrier.clientSecret,
						accountNumber: carrier.accountNumber,
					},
				};
			} else if (carrier.carrierType.name === carrierTypes.ups) {
				carrierCredentials = {
					ups: {
						password: carrier.password,
						username: carrier.username, // TODO: Remove after REST API is stable
						shipperNumber: carrier.shipperNumber,
						accessLicenseNumber: carrier.accessLicenseNumber,
						refreshToken: carrier.refreshToken,
					},
				};
			} else if (carrier.carrierType.name === carrierTypes.stamps) {
				carrierCredentials = {
					stamps: {
						refreshToken: carrier.refreshToken,
					},
				};
			}
		}

		return carrierCredentials;
	};

	@action.bound async getCarrierTypes() {
		await this.lookupStore.find({ page: 1, rpp: 50 }, lookupRouteName.carrierType);
		this.carrierTypeOptions = _.map(this.lookupStore.data, item => {
			return { value: item.id, label: item.name, extra: item };
		});
	}

	@action.bound async getCarrierShippingAccounts() {
		const carrierFilter =
			this.getEditMode() === 'edit'
				? {
						rpp: 100,
						page: 1,
						IsThirdParty: false,
						ExcludedIds: [this.rootStore.routerStore.routerState.queryParams.editId],
						CarrierTypeIds: this.carrier.isPartialThirdParty ? null : [this.carrier.carrierTypeId],
						embed: ['carrierType'],
				  }
				: {
						rpp: 100,
						page: 1,
						IsThirdParty: false,
						embed: ['carrierType'],
				  };

		await this.carrierStore.find(carrierFilter);
		if (this.isSuperAdminOrganization && this.isEditMode) {
			this.shippingAccountOptions = _.reduce(
				this.carrierStore.response.item,
				(results, item) => {
					if (item.sellerId === this.carrier.sellerId) {
						results.push({ value: item.id, label: item.name, extra: item });
					}
					return results;
				},
				[]
			);
		} else {
			this.shippingAccountOptions = _.map(this.carrierStore.response.item, item => {
				return { value: item.id, label: item.name, extra: item };
			});
		}
	}

	@action.bound async createOrUpdateCarrier() {
		const {
			ups,
			fedex,
			stamps,
			carrierType,
			name,
			isPartialThirdParty,
			shippingAccount,
			thirdPartyBilling,
			surplusCharge,
			isPercentage,
			seller,
			carrierWarehouses,
		} = this.form.values();
		try {
			let carrierCredentials = {};
			if (this.isThirdParty && isPartialThirdParty) {
				carrierCredentials = {
					thirdPartyBilling: thirdPartyBilling,
				};
			} else if (carrierType.label === carrierTypes.stamps) {
				carrierCredentials = stamps;
			} else if (carrierType.label === carrierTypes.fedex) {
				carrierCredentials = fedex;
			} else if (carrierType.label === carrierTypes.ups) {
				carrierCredentials = ups;
				if (this.isExternalAccount) {
					this.isAuthorized = true;
				}
				carrierCredentials.isExternalAccount = this.isExternalAccount;
			}

			removeNullOrEmptyStringProperties(carrierCredentials);

			const carrierWarehousesToSend = _.map(carrierWarehouses, warehouse => {
				return {
					warehouseId: warehouse.value,
				};
			});

			const mode = this.getEditMode();
			if (mode === 'edit') {
				await this.carrierStore.updateCarrier(
					{
						sellerId: this.carrierStore.item.sellerId,
						id: this.rootStore.routerStore.routerState.queryParams.editId,
						name: name,
						carrierWarehouses: carrierWarehousesToSend,
						surplusCharge: surplusCharge && surplusCharge != '' ? surplusCharge : 0,
						isPercentage: isPercentage,
						carrierTypeId:
							this.isThirdParty && isPartialThirdParty ? shippingAccount.extra.carrierTypeId : carrierType.value,
						isThirdParty: this.isThirdParty,
						isPartialThirdParty: this.isThirdParty && isPartialThirdParty,
						shippingAccountId: this.isThirdParty && isPartialThirdParty ? shippingAccount.value : null,
						...carrierCredentials,
					},
					sourceOfChange.updateCarrier
				);
				this.rootStore.notificationStore.success(['LOOKUPUPDATE.CARRIER', { name: name }]);
				this.parentView.queryUtility.initialize(false);
				this.parentView.closeEditModal();
			} else if (mode === 'create') {
				if (
					!this.isAuthorized &&
					(this.form.$('carrierType').value.label === carrierTypes.stamps ||
						this.form.$('carrierType').value.label === carrierTypes.ups)
				) {
					this.form.invalidate(translate('MESSAGES.REQUIRED.CARRIERVIEW.ISAUTHORIZED'));
				} else {
					await this.carrierStore.createCarrier(
						{
							sellerId: seller != null ? seller.id : null,
							name: name,
							carrierWarehouses: carrierWarehousesToSend,
							surplusCharge: surplusCharge && surplusCharge != '' ? surplusCharge : 0,
							isPercentage: isPercentage,
							carrierTypeId:
								this.isThirdParty && isPartialThirdParty ? shippingAccount.extra.carrierTypeId : carrierType.value,
							isThirdParty: this.isThirdParty,
							isPartialThirdParty: this.isThirdParty && isPartialThirdParty,
							shippingAccountId: this.isThirdParty && isPartialThirdParty ? shippingAccount.value : null,
							abrv: encodeURIComponent(name).toLowerCase(),
							...carrierCredentials,
						},
						sourceOfChange.createCarrier
					);
					this.rootStore.notificationStore.success(['LOOKUPCREATE.CARRIER', { name: name }]);
					this.parentView.queryUtility.initialize(false);
					this.parentView.closeEditModal();
				}
			}
		} catch (err) {
			formInvalidate({ form: this.form, err });
		}
	}

	@action.bound
	onToggleThirdParty = async isThirdParty => {
		this.isThirdParty = isThirdParty;
		this.isPartialThirdParty = !isThirdParty ? false : this.form.$('isPartialThirdParty').value;
		this.setBillToFieldsRequired(this.form.$('isPartialThirdParty').value);
	};

	@action.bound
	isPartialThirdPartyCheck = async (event, field) => {
		field.value = event.currentTarget.checked;
		this.isPartialThirdParty = event.currentTarget.checked;
		if (!this.isEditMode) {
			this.form.$('shippingAccount').reset();
			this.form.$('carrierType').reset();
		}
		this.setBillToFieldsRequired(this.form.$('isPartialThirdParty').value);
	};

	@action.bound
	setBillToFieldsRequired = async isPartialThirdParty => {
		this.resetBillToFieldsRules();
		if (this.isThirdParty && isPartialThirdParty) {
			this.form.$('carrierType').set('rules', '');
			this.form.$('shippingAccount').set('rules', 'required');
			this.form.$('thirdPartyBilling.billToAccountNumber').set('rules', 'required');
			this.form.$('thirdPartyBilling.billToAccountZip').set('rules', 'required');
		} else {
			this.form.$('carrierType').set('rules', 'required');
		}

		if (this.form.$('carrierType').value.label === carrierTypes.fedex) {
			// If is Fedex third party and partial third party, the Client Id, Client Secret and Account Number are used from master account (shipping account) and are not required.
			if (this.isThirdParty && this.isPartialThirdParty) {
				this.setFedexRules(rules.notRequired);
			}
			// If is Fedex master or third party account, Client Id, Client Secret and Account Number are required.
			else {
				this.setFedexRules(rules.required);
			}
		} else {
			this.setFedexRules(rules.notRequired);
		}
	};

	@action.bound
	setFedexRules = value => {
		this.form.$('fedex.clientId').set('rules', `${value}`);
		this.form.$('fedex.clientSecret').set('rules', `${value}`);
		this.form.$('fedex.accountNumber').set('rules', `${value}`);
	};

	@action
	setUpsRules = value => {
		this.form.$('ups.clientId').set('rules', value);
		this.form.$('ups.clientSecret').set('rules', value);
		this.form.$('ups.shipperNumber').set('rules', value);
		this.form.$('ups.refreshToken').set('rules', value);
	};

	@action.bound
	resetBillToFieldsRules() {
		this.form.$('shippingAccount').set('rules', '');
		this.form.$('thirdPartyBilling.billToAccountNumber').set('rules', '');
		this.form.$('thirdPartyBilling.billToAccountZip').set('rules', '');
	}

	getEditMode() {
		const queryParams = this.rootStore.routerStore.routerState.queryParams;
		const mode = queryParams['editId'] !== undefined ? 'edit' : queryParams['create'] !== undefined ? 'create' : 'none';
		this.isEditMode = mode === 'edit' ? true : false;
		return mode;
	}

	clearApiFields = () => {
		this.form.$('ups').clear();
		this.form.$('fedex').clear();
	};

	@action.bound
	onSelectChange = (selectedOption, field, clearApiFields = false) => {
		field.value = selectedOption;
		if (selectedOption.label === carrierTypes.fedex) {
			this.setFedexRules(rules.required);
			this.setUpsRules(rules.notRequired);
		} else {
			this.setFedexRules(rules.notRequired);
		}
		if (clearApiFields) {
			this.clearApiFields();
		}
		if (this.isThirdParty && this.isPartialThirdParty) {
			this.form.$('carrierType').reset();
		} else {
			this.form.$('shippingAccount').reset();
		}
	};

	@action.bound
	onSelectSeller = async (selectedOption, field) => {
		field.value = selectedOption;
		if (this.isSuperAdminOrganization) {
			this.shippingAccountOptions = _.reduce(
				this.carrierStore.response.item,
				(results, item) => {
					if (item.sellerId === selectedOption.id) {
						results.push({ value: item.id, label: item.name, extra: item });
					}
					return results;
				},
				[]
			);
		}
		this.warehouseDropdownStore.filter.sellerWarehousesOnlyOrganizationId = selectedOption.id;
		this.warehouseDropdownStore.filter.includeOnlySellerWarehouses = true;
		this.warehouseDropdownStore.setValue(null);
		await this.warehouseDropdownStore.reload();
		this.form.$('shippingAccount').reset();
	};

	@action.bound
	async onAuthorizeStamps() {
		try {
			await this.carrierStore.authorizeStamps();
			this.authorizedOAuthConfirmModal.open({
				message: ['OAUTH.STAMPS.CONFIRMSUCCESSAUTH'],
			});
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
	}

	authorizedOAuthConfirmModal = new ConfirmModalParams({
		onConfirm: async () => {
			// Get authorization results from Redis.
			try {
				var response = await this.carrierStore.getAuthorizationResultStamps();
				if (isSome(response)) {
					this.form.$('stamps.refreshToken').value = response.data;
					this.isAuthorized = true;
					this.rootStore.notificationStore.success(['OAUTH.STAMPS.SUCCESSFORMUPDATE']);
				} else {
					this.rootStore.notificationStore.error(['OAUTH.STAMPS.FAILGETREFRESHTOKEN']);
				}
			} catch (err) {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
		},
	});

	@action.bound
	async onAuthorizeUps() {
		try {
			await this.carrierStore.authorizeUps();
			this.authorizedOAuthConfirmModalUps.open({
				message: ['OAUTH.UPS.CONFIRMSUCCESSAUTH'],
			});
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
	}

	authorizedOAuthConfirmModalUps = new ConfirmModalParams({
		onConfirm: async () => {
			// Get Refresh Token from Redis.
			try {
				var response = await this.carrierStore.getAuthorizationResultUps();
				if (isSome(response)) {
					this.form.$('ups.refreshToken').value = response.data;
					this.isAuthorized = true;
					this.rootStore.notificationStore.success(['OAUTH.UPS.SUCCESSFORMUPDATE']);
				} else {
					this.rootStore.notificationStore.error(['OAUTH.UPS.FAILGETREFRESHTOKEN']);
				}
			} catch (err) {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
		},
	});

	@action
	setIsExternalAccount = isExternalAccount => {
		this.isExternalAccount = isExternalAccount;
	};
}

const rules = {
	notRequired: '',
	required: 'required',
};

export default CarrierEditViewStore;



// WEBPACK FOOTER //
// ./src/modules/carrier/stores/CarrierEditViewStore.js